import { Principal } from '@dfinity/principal'
import { JsonableSecp256k1Identity, Secp256k1KeyIdentity } from '@dfinity/identity-secp256k1'

import {
  ApproveErc20Params,
  ApproveParams,
  BTCSendParams,
  SolSendParams,
  CreateWalletResponse,
  ExportKeychainParams,
  ImportWalletResponse,
  MintCkEthParams,
  NftSendParams,
  SendErc20Params,
  SendEthParams,
  SendParams,
  XTCBurnParams,
  walletActions,
  WalletStatusResponse,
  BTCSendOrdinalParams,
  EthCallContractParams,
  CustomApproveErc20Params,
} from '../../interfaces/walletActions'
import { useCurrentWallet } from '../../state'
import { callWallet } from './callWallet'

const performWalletAction = (action: string, params?: any) => {
  const { currentWallet } = useCurrentWallet.getState()

  return callWallet(action, currentWallet, params)
}

export const getStatus = async () => {
  return (await performWalletAction('status', {})) as WalletStatusResponse
}

export const lock = async () => {
  return (await performWalletAction(walletActions.lock)) as boolean
}

export const unlock = async (password: string) => {
  return (await performWalletAction(walletActions.unlock, {
    password,
  })) as boolean
}

export const createWallet = async () => {
  return (await performWalletAction(walletActions.create)) as CreateWalletResponse
}

export const importWallet = async (mnemonic: string) => {
  return (await performWalletAction(walletActions.import, {
    mnemonic,
  })) as ImportWalletResponse
}

export const changePassword = async (password: string) => {
  return await performWalletAction(walletActions.changePassword, { password })
}

export const clearKeychain = async () => {
  return await performWalletAction(walletActions.clearKeychain)
}

export const transferToken = async (params: SendParams) => {
  return await performWalletAction(walletActions.transferToken, params)
}

export const transferEth = async (params: SendEthParams) => {
  return await performWalletAction(walletActions.transferEth, params)
}

export const transferErc20 = async (params: SendErc20Params) => {
  return await performWalletAction(walletActions.transferErc20, params)
}

export const approveErc20 = async (params: ApproveErc20Params) => {
  return await performWalletAction(walletActions.approveErc20, params)
}

export const ethCallContract = async (params: EthCallContractParams) => {
  return await performWalletAction(walletActions.ethCallContract, params)
}

export const customApproveErc20 = async (params: CustomApproveErc20Params) => {
  return await performWalletAction(walletActions.customApproveErc20, params)
}

export const depositErc20 = async (params: ApproveErc20Params) => {
  return await performWalletAction(walletActions.depositErc20, params)
}

export const sendBTC = async (params: BTCSendParams) => {
  return await performWalletAction(walletActions.transferBTC, params)
}

export const sendOrdinal = async (params: BTCSendOrdinalParams) => {
  return await performWalletAction(walletActions.transferOrdinal, params)
}

export const sendSol = async (params: SolSendParams) => {
  return await performWalletAction(walletActions.sendSol, params)
}

export const mintCKEth = async (params: MintCkEthParams) => {
  return await performWalletAction(walletActions.mintCkETH, params)
}

export const approveToken = async (params: ApproveParams) => {
  return await performWalletAction(walletActions.approveToken, params)
}

export const sendNFT = async (params: NftSendParams) => {
  return await performWalletAction(walletActions.sendNFT, params)
}

export const exportKeychain = async (params: ExportKeychainParams) => {
  return (await performWalletAction(walletActions.exportKeychain, params)) as string
}

export const exportPem = async (params: ExportKeychainParams) => {
  return (await performWalletAction(walletActions.exportPem, params)) as string
}

export const importKeypairWallet = async (keypair: string, keyType?: string) => {
  return (await performWalletAction(walletActions.importPem, {
    keypair,
    keyType,
  })) as ImportWalletResponse
}

export const burnXTC = async (params: XTCBurnParams) => {
  return await performWalletAction(walletActions.burnXTC, params)
}

export const getPrincipals = async () => {
  const principals = (await performWalletAction(walletActions.getPrincipals)) as string[]

  return principals.map((p) => Principal.from(p))
}

export const getIdentity = async (principal: string) => {
  const jsonIdentity = (await performWalletAction(walletActions.getIdentity, {
    principal,
  })) as JsonableSecp256k1Identity

  return Secp256k1KeyIdentity.fromParsedJson(jsonIdentity)
}

export const addNewAccount = async (principal: string) => {
  const newPrincipal = (await performWalletAction(walletActions.addAccount, {
    principal,
  })) as string
  return Principal.from(newPrincipal)
}

export const signMessage = async (principalId: string, message: string) => {
  return performWalletAction(walletActions.signMessage, {
    principalId,
    message,
  }) as Promise<{ publicKey: string; signature: string }>
}
